import { useEffect, useRef } from "react";

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          "expired-callback": () => void;
          "error-callback": () => void;
          language?: string;
        },
      ) => string;
      remove: (widgetId: string) => void;
    };
  }
}

interface TurnstileProps {
  siteKey?: string;
  onToken: (token: string) => void;
  onExpire: () => void;
}

export function Turnstile({ siteKey, onToken, onExpire }: TurnstileProps) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!siteKey || !ref.current) return;

    let widgetId: string | null = null;
    const render = () => {
      if (!window.turnstile || !ref.current || widgetId) return;
      widgetId = window.turnstile.render(ref.current, {
        sitekey: siteKey,
        callback: onToken,
        "expired-callback": onExpire,
        "error-callback": onExpire,
        language: "th",
      });
    };

    if (!document.querySelector('script[src^="https://challenges.cloudflare.com/turnstile"]')) {
      const script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.onload = render;
      document.head.appendChild(script);
    } else {
      render();
    }

    const interval = window.setInterval(render, 250);
    return () => {
      window.clearInterval(interval);
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [onExpire, onToken, siteKey]);

  if (!siteKey) return null;
  return <div className="turnstile-box" ref={ref} />;
}
